import React from 'react';
import { reportCaughtError } from '../utils/clientErrorReporting';
import {
  isRecoverableFirestoreAssertion,
} from '../utils/firestoreAssertionRecovery';

const FIRESTORE_RECOVERY_SESSION_KEY = 'memberNest.firestoreAssertionRecoveryV1';
const FIRESTORE_RECOVERY_WINDOW_MS = 60 * 1000;

function getRouteKey() {
  if (typeof window === 'undefined') return 'unknown';
  return `${window.location.pathname || '/'}${window.location.search || ''}`;
}

function readRecoveryState() {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(window.sessionStorage.getItem(FIRESTORE_RECOVERY_SESSION_KEY) || 'null');
  } catch (err) {
    return null;
  }
}

function markRecoveryAttempted(routeKey) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(FIRESTORE_RECOVERY_SESSION_KEY, JSON.stringify({
      routeKey,
      attemptedAt: Date.now(),
    }));
  } catch (err) {}
}

function shouldReloadForFirestoreRecovery(routeKey) {
  const state = readRecoveryState();
  const attemptedAt = Number(state?.attemptedAt || 0);
  return state?.routeKey !== routeKey || Date.now() - attemptedAt > FIRESTORE_RECOVERY_WINDOW_MS;
}

function reloadPage() {
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      recoverableFirestoreAssertion: false,
      recoveryReloading: false,
    };
    this.reloadTimer = null;
  }
  static getDerivedStateFromError(error) {
    return {
      error,
      recoverableFirestoreAssertion: isRecoverableFirestoreAssertion(error),
    };
  }
  componentDidCatch(error, info) {
    const recoverableFirestoreAssertion = isRecoverableFirestoreAssertion(error);
    const routeKey = getRouteKey();
    const shouldReload = recoverableFirestoreAssertion && shouldReloadForFirestoreRecovery(routeKey);

    if (!recoverableFirestoreAssertion) {
      reportCaughtError(error, {
        source: `react.${this.props.label || 'ErrorBoundary'}`,
        severity: 'fatal',
        componentStack: info?.componentStack || '',
        context: {},
      });
    }

    if (shouldReload) {
      markRecoveryAttempted(routeKey);
      this.setState({ recoveryReloading: true });
      this.reloadTimer = window.setTimeout(reloadPage, 300);
    }
  }
  componentWillUnmount() {
    if (this.reloadTimer && typeof window !== 'undefined') {
      window.clearTimeout(this.reloadTimer);
    }
  }
  render() {
    if (this.state.error) {
      if (this.state.recoverableFirestoreAssertion) {
        return <div style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '1rem',
          background: '#f8fafc',
          color: '#0f172a'
        }}>
          <div style={{
            width: 'min(100%, 520px)',
            border: '1px solid #dbe3ef',
            borderRadius: '8px',
            background: '#fff',
            padding: '1rem',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)'
          }}>
            <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem' }}>Refreshing Member Nest</h1>
            <p style={{ margin: '0 0 1rem', color: '#475569' }}>
              {this.state.recoveryReloading
                ? 'The app hit a temporary browser data sync issue and is refreshing.'
                : 'The app hit a temporary browser data sync issue. Refreshing usually clears it.'}
            </p>
            <button type="button" className="button" onClick={reloadPage}>
              Reload Member Nest
            </button>
          </div>
        </div>;
      }

      return (
        <div role="alert" style={{
          padding: '1rem',
          border: '1px solid #fecaca',
          borderRadius: '0.5rem',
          color: '#991b1b',
          background: '#fff1f2',
        }}>
          Something went wrong. Refresh the page to try again.
        </div>
      );
    }
    return this.props.children;
  }
}
